import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { setField } from "../../../../redux/improvementPlanSlice";
import { RootState } from "../../../../redux/store";
import { ImprovementPlanFormDto } from "../../types";
import { convertFileToBase64, downloadBase64File, getContentTypeFromExtension } from "../../../../utils/formatters/FileFormatter";
import DocumentsWorkspace from "../../../../components/DocumentsPanels/DocumentsWorkspace";
import { useDocumentsPreview } from "../../../../components/DocumentsPanels/useDocumentsPreview";
import { DocumentPanelItem } from "../../../../components/DocumentsPanels/types";
import { getDocumentContentType } from "../../../../components/DocumentsPanels/documentUtils";
import { SectionPermissionTypeEnum } from "../../../../types/SectionPermissionTypesEnum";
import ModalGlobal from "../../../../components/AppModal/ModalGlobal";
import { useModal } from "../../../../hooks/useModal";
import AlertMessage from "../../../../components/AlertMessage";
import { useAlert } from "../../../../hooks/useAlert";

interface ImprovementPlanDocument extends DocumentPanelItem {
    index: number;
}

export default function ImprovementPlanDocumentsTab() {
    const { t } = useTranslation(["improvementPlan", "common"]);
    const dispatch = useDispatch();
    const planFiles = useSelector((state: RootState) => state.improvementPlanForm.data.files);
    const files = useMemo(() => planFiles ?? [], [planFiles]);

    const { alert, hideAlert, isVisible } = useAlert({ fromLocation: false });
    const { showModal, modalData, closeModal, isLoading, errorModal, currentItem, openModal } = useModal<ImprovementPlanDocument>();
    const preview = useDocumentsPreview<ImprovementPlanDocument>();

    const documents = useMemo<ImprovementPlanDocument[]>(() => files.map((file, index) => ({
        documentKey: `plan-file-${index}-${file.name}`,
        index,
        id: file.id || undefined,
        fileName: file.name,
        contentType: file.type || getContentTypeFromExtension(file.name?.split(".").pop()),
        data: file.content,
    })), [files]);

    const updateFiles = (nextFiles: ImprovementPlanFormDto["files"]) =>
        dispatch(setField({ field: "files", value: nextFiles }));

    const handleLoadFiles = async (selectedFiles: File[]) => {
        if (selectedFiles.length === 0) return;

        const filesToAdd = await Promise.all(selectedFiles.map(async (file) => ({
            id: 0,
            name: file.name,
            type: file.type || "application/octet-stream",
            content: await convertFileToBase64(file),
        })));

        updateFiles([...files, ...filesToAdd] as ImprovementPlanFormDto["files"]);

        const [firstFile] = filesToAdd;
        if (firstFile) {
            void preview.previewDocument({
                documentKey: `plan-file-${files.length}-${firstFile.name}`,
                index: files.length,
                fileName: firstFile.name,
                contentType: firstFile.type,
                data: firstFile.content,
            });
        }
    };

    const handleDelete = (document: ImprovementPlanDocument) =>
        openModal(
            document,
            t("common:documents.deleteDocumentTitle"),
            t("common:documents.deleteDocumentSubtitle"),
            "deleteWithoutInput",
            ""
        );

    const confirmDelete = () => {
        if (!currentItem) return;

        const nextFiles = files.filter((_, index) => index !== currentItem.index);
        updateFiles(nextFiles as ImprovementPlanFormDto["files"]);

        if (preview.previewedDocument?.index === currentItem.index) {
            preview.resetPreview();
        }

        closeModal();
    };

    const handleDownload = (document: ImprovementPlanDocument) => {
        if (!document.data) return;
        downloadBase64File(
            document.fileName ?? "file",
            document.data,
            getDocumentContentType(document) ?? "application/octet-stream"
        );
    };

    return (
        <>
            <ModalGlobal
                show={showModal}
                onClose={closeModal}
                title={modalData.title}
                contentType={modalData.type}
                subtitle={modalData.subtitle}
                subtitle2={modalData.subtitle2}
                saveText={t("common:modalActionOK")}
                closeText={t("common:modalActionNOTOK")}
                error={errorModal?.endDate || errorModal?.errorModal}
                onSave={confirmDelete}
                isLoading={isLoading}
            />
            {isVisible && <AlertMessage message={alert!.message} type={alert!.type} onClose={hideAlert} />}

            <div className="d-flex flex-column" style={{ height: "70vh", minHeight: 0 }}>
                <DocumentsWorkspace<ImprovementPlanDocument>
                    documents={documents}
                    preview={preview}
                    sectionPermission={SectionPermissionTypeEnum.IMPROVEMENT_PLANS}
                    onLoadFiles={handleLoadFiles}
                    onDelete={handleDelete}
                    onDownload={handleDownload}
                />
            </div>
        </>
    );
}
